import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const GSI_SRC = 'https://accounts.google.com/gsi/client';
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// The Google script is fetched once per page, however many buttons render.
let scriptPromise = null;

const loadGoogleScript = () => {
  if (window.google && window.google.accounts && window.google.accounts.id) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GSI_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        scriptPromise = null; // let a later attempt retry
        reject(new Error('gsi-unavailable'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

/** Renders Google's own sign-in button and exchanges its credential for a session. */
const GoogleSignInButton = ({ onSignedIn }) => {
  const { isDarkMode } = useTheme();
  const { t, i18n } = useTranslation();
  const { signIn } = useAuth();
  const containerRef = useRef(null);
  const [status, setStatus] = useState('loading');
  const [errorCode, setErrorCode] = useState(null);

  const handleCredential = useCallback(async (response) => {
    setStatus('signing');
    setErrorCode(null);
    try {
      const user = await signIn(response.credential);
      if (onSignedIn) onSignedIn(user);
    } catch (error) {
      setErrorCode(error.code || 'server-error');
      setStatus('ready');
    }
  }, [signIn, onSignedIn]);

  useEffect(() => {
    if (!CLIENT_ID) {
      setStatus('unconfigured');
      return undefined;
    }

    let cancelled = false;
    let check = null;
    loadGoogleScript()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        const { id } = window.google.accounts;
        id.initialize({ client_id: CLIENT_ID, callback: handleCredential });
        containerRef.current.innerHTML = '';
        id.renderButton(containerRef.current, {
          theme: isDarkMode ? 'filled_black' : 'outline',
          size: 'large',
          shape: 'pill',
          text: 'signin_with',
          locale: i18n.resolvedLanguage,
          width: 280,
        });
        setStatus('ready');

        // When the page's origin is missing from the OAuth client's authorised
        // list, Google logs to the console and draws nothing at all. Catch that
        // silence and say so, instead of showing an empty box.
        check = setTimeout(() => {
          if (!cancelled && containerRef.current && containerRef.current.childElementCount === 0) {
            setStatus('blocked');
          }
        }, 2500);
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
      clearTimeout(check);
    };
    // Re-rendering on theme or language change keeps Google's button in step
    // with the rest of the page.
  }, [handleCredential, isDarkMode, i18n.resolvedLanguage]);

  if (status === 'unconfigured') {
    return <p className={`text-sm ${isDarkMode ? 'text-light/60' : 'text-gray-500'}`}>{t('auth.notConfigured')}</p>;
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <div ref={containerRef} className="min-h-[44px] flex items-center justify-center" />

      {status === 'loading' && (
        <p className={`text-sm ${isDarkMode ? 'text-light/60' : 'text-gray-500'}`}>{t('auth.loading')}</p>
      )}
      {status === 'signing' && (
        <p className={`text-sm ${isDarkMode ? 'text-primary' : 'text-green-600'}`}>{t('auth.signingIn')}</p>
      )}
      {status === 'error' && (
        <p className="text-sm text-red-500">{t('auth.errors.gsi-unavailable')}</p>
      )}
      {status === 'blocked' && (
        <p className="text-sm text-amber-500 max-w-xs text-center">
          {t('auth.errors.origin-blocked', { origin: window.location.origin })}
        </p>
      )}
      {errorCode && (
        <p className="text-sm text-red-500">
          {t([`auth.errors.${errorCode}`, 'auth.errors.server-error'])}
        </p>
      )}
    </div>
  );
};

export default GoogleSignInButton;
